import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import InlineCropper from '../InlineCropper';
import BackgroundStyleSelector from './BackgroundStyleSelector';

function GameRecapFields({
  formData,
  onChange,
  template,
  roster,
  showRosterDropdown,
  onPlayerSearch,
  onPlayerSelect,
  onRosterDropdownChange,
  formatPlayerDisplay,
  // Dominant image
  dominantImageCropping,
  dominantImageTempImage,
  dominantImagePreview,
  saving,
  downloading,
  onDominantImageChange,
  onDominantImageCropComplete,
  onDominantImageCropCancel,
  onEditDominantImageCrop,
  onRemoveDominantImage,
  canUseCutouts,
  onRemoveDominantBg,
  isRemoving,
  bgRemovalError,
  originalDominantImage,
  cutoutDominantImage,
  activeDominantImageVersion,
  onToggleDominantImageVersion,
  // Background photo
  backgroundCropping,
  backgroundTempImage,
  backgroundPhotoPreview,
  onBackgroundPhotoChange,
  onBackgroundCropComplete,
  onBackgroundCropCancel,
  onEditBackgroundCrop,
  onRemoveBackgroundPhoto,
  onSetBackgroundPhotoFromUrl,
  onDateFormatClick,
  backgroundStyle,
  onBackgroundStyleChange,
  teamColors,
}) {
  const [bgMode, setBgMode] = useState(() => {
    if (!backgroundPhotoPreview) return 'team-colors';
    if (backgroundPhotoPreview.includes('pexels.com')) return 'pexels';
    return 'upload';
  });
  const [pexelsQuery, setPexelsQuery] = useState('baseball stadium');
  const [pexelsResults, setPexelsResults] = useState([]);
  const [pexelsLoading, setPexelsLoading] = useState(false);
  const [pexelsLoadingMore, setPexelsLoadingMore] = useState(false);
  const [pexelsPage, setPexelsPage] = useState(1);
  const [pexelsTotalResults, setPexelsTotalResults] = useState(0);

  useEffect(() => {
    if (!backgroundPhotoPreview) return;
    if (backgroundPhotoPreview.includes('pexels.com')) setBgMode('pexels');
    else setBgMode('upload');
  }, [backgroundPhotoPreview]);

  async function searchPexels(query, page = 1, append = false) {
    if (append) setPexelsLoadingMore(true);
    else setPexelsLoading(true);
    try {
      const res = await api.get('/pexels/search', { params: { query, page, per_page: 12 } });
      const photos = res.data.photos || [];
      setPexelsTotalResults(res.data.total_results || 0);
      setPexelsPage(page);
      if (append) {
        setPexelsResults((prev) => {
          const combined = [...prev, ...photos];
          return combined.filter((p, i, self) => i === self.findIndex((x) => x.id === p.id));
        });
      } else {
        setPexelsResults(photos);
      }
    } catch (err) {
      console.error('Pexels search failed:', err);
      if (!append) setPexelsResults([]);
    } finally {
      if (append) setPexelsLoadingMore(false);
      else setPexelsLoading(false);
    }
  }

  function handlePexelsSearch() {
    searchPexels(pexelsQuery, 1, false);
  }

  function handlePexelsLoadMore() {
    searchPexels(pexelsQuery, pexelsPage + 1, true);
  }

  return (
    <>
      <div className="form-group">
        <label htmlFor="heroFont">Hero Font</label>
        <select
          id="heroFont"
          name="heroFont"
          value={formData.heroFont || 'Bebas Neue'}
          onChange={onChange}
        >
          {[
            { value: 'Bebas Neue', label: 'Bebas Neue' },
            { value: 'Oswald', label: 'Oswald' },
            { value: 'Russo One', label: 'Russo One' },
            { value: 'Black Ops One', label: 'Black Ops One' },
            { value: 'Teko', label: 'Teko' },
            { value: 'Orbitron', label: 'Orbitron' },
          ].map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="heroFontSize">
          Result Size
          <span style={{ fontSize: '12px', color: '#718096', marginLeft: '8px' }}>
            ({formData.heroFontSize || 280}px)
          </span>
        </label>
        <input
          id="heroFontSize"
          name="heroFontSize"
          type="range"
          min={120}
          max={340}
          step={10}
          value={formData.heroFontSize || 280}
          onChange={onChange}
        />
      </div>

      <div className="form-group">
        <label htmlFor="accentBlockColor">Accent Color</label>
        <input
          id="accentBlockColor"
          name="accentBlockColor"
          type="color"
          value={formData.accentBlockColor || '#cccccc'}
          onChange={onChange}
        />
      </div>

      <div className="form-group">
        <label htmlFor="detailTextColor">Detail Text Color</label>
        <input
          id="detailTextColor"
          name="detailTextColor"
          type="color"
          value={formData.detailTextColor || '#ffffff'}
          onChange={onChange}
        />
      </div>
      <BackgroundStyleSelector
        backgroundStyle={backgroundStyle}
        teamColors={teamColors}
        onBackgroundStyleChange={onBackgroundStyleChange}
      />
      <div className="form-group">
        <label htmlFor="gameDate">Game Date *</label>
        <input
          id="gameDate"
          name="gameDate"
          type="date"
          value={formData.gameDate}
          onChange={onChange}
          placeholder="Select date"
          required
        />
        <button
          type="button"
          className="format-link"
          onClick={onDateFormatClick}
          style={{
            background: 'none',
            border: 'none',
            color: '#667eea',
            fontSize: '13px',
            cursor: 'pointer',
            marginTop: '4px',
            padding: '0',
            textDecoration: 'underline',
          }}
        >
          Change date format
        </button>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="homeScore">Your Score *</label>
          <input
            id="homeScore"
            name="homeScore"
            type="number"
            value={formData.homeScore}
            onChange={onChange}
            placeholder="-"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="awayScore">Opponent Score *</label>
          <input
            id="awayScore"
            name="awayScore"
            type="number"
            value={formData.awayScore}
            onChange={onChange}
            placeholder="-"
            required
          />
        </div>
      </div>

      <div className="form-group player-search-group">
        <label htmlFor="topPerformer">
          Player of the Game
          {roster.length > 0 && (
            <span
              style={{
                fontSize: '12px',
                color: '#718096',
                fontWeight: 'normal',
                marginLeft: '8px',
              }}
            >
              (Select from roster or type custom name)
            </span>
          )}
        </label>
        <div className="player-search-container">
          <input
            id="topPerformer"
            name="topPerformer"
            type="text"
            value={formData.topPerformer}
            onChange={onPlayerSearch}
            onFocus={() => onRosterDropdownChange(true)}
            onBlur={() => {
              setTimeout(() => onRosterDropdownChange(false), 200);
            }}
            placeholder={
              roster.length > 0
                ? 'Search roster or type name...'
                : 'Johnny Smith'
            }
            autoComplete="off"
          />
          {showRosterDropdown && roster.length > 0 && (
            <div className="roster-dropdown">
              {roster.map((player) => (
                <div
                  key={player.id}
                  className="roster-dropdown-item"
                  onClick={() => onPlayerSelect(player)}
                >
                  {formatPlayerDisplay(player)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="topPerformerStats">
          Player Stats
          <span
            style={{
              fontSize: '12px',
              color: '#718096',
              marginLeft: '8px',
            }}
          >
            ({formData.topPerformerStats.length}/100)
          </span>
        </label>
        <input
          id="topPerformerStats"
          name="topPerformerStats"
          type="text"
          value={formData.topPerformerStats}
          onChange={onChange}
          placeholder="Enter stats"
          maxLength={100}
        />
      </div>

      {/* ── Background Section ── */}
      <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginTop: '8px' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#4a5568', marginBottom: '12px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          Background
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <button
            type="button"
            className={bgMode === 'team-colors' ? 'btn-primary' : 'btn-secondary'}
            style={{ flex: 1, padding: '8px 4px', fontSize: '12px' }}
            onClick={() => { setBgMode('team-colors'); if (backgroundPhotoPreview) onRemoveBackgroundPhoto(); }}
          >
            Team Colors
          </button>
          <button
            type="button"
            className={bgMode === 'upload' ? 'btn-primary' : 'btn-secondary'}
            style={{ flex: 1, padding: '8px 4px', fontSize: '12px' }}
            onClick={() => setBgMode('upload')}
          >
            Upload Photo
          </button>
          <button
            type="button"
            className={bgMode === 'pexels' ? 'btn-primary' : 'btn-secondary'}
            style={{ flex: 1, padding: '8px 4px', fontSize: '12px' }}
            onClick={() => { setBgMode('pexels'); if (!pexelsResults.length) searchPexels('baseball stadium'); }}
          >
            Browse Free
          </button>
        </div>

        {bgMode === 'upload' && (
          <div className="form-group">
            {backgroundCropping ? (
              <InlineCropper
                imageSrc={backgroundTempImage}
                onCropComplete={onBackgroundCropComplete}
                onCancel={onBackgroundCropCancel}
              />
            ) : backgroundPhotoPreview && !backgroundPhotoPreview.includes('pexels.com') ? (
              <div className="background-photo-preview">
                <img src={backgroundPhotoPreview} alt="Background" />
                <button type="button" className="btn-edit-photo-overlay" onClick={onEditBackgroundCrop} disabled={saving || downloading}>
                  Edit
                </button>
                <button type="button" className="btn-remove-background-photo" onClick={onRemoveBackgroundPhoto} disabled={saving || downloading}>
                  ✕ Remove
                </button>
              </div>
            ) : (
              <label htmlFor="game-recap-bg-photo" className="background-photo-upload-btn">
                <span>Upload Background Photo</span>
                <input
                  id="game-recap-bg-photo"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={onBackgroundPhotoChange}
                  style={{ display: 'none' }}
                  disabled={saving || downloading}
                />
              </label>
            )}
          </div>
        )}

        {bgMode === 'pexels' && (
          <div className="form-group">
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <input
                type="text"
                value={pexelsQuery}
                onChange={(e) => setPexelsQuery(e.target.value)}
                placeholder="baseball stadium, field…"
                style={{ flex: 1 }}
              />
              <button type="button" onClick={handlePexelsSearch} className="btn-secondary" disabled={pexelsLoading} style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                {pexelsLoading ? '…' : 'Search'}
              </button>
            </div>

            {backgroundPhotoPreview?.includes('pexels.com') && (
              <div className="brag-card-unsplash-active">
                <img src={backgroundPhotoPreview} alt="Selected background" crossOrigin="anonymous" />
                <button type="button" className="brag-card-remove-btn" onClick={onRemoveBackgroundPhoto}>
                  Remove
                </button>
              </div>
            )}

            {!pexelsLoading && pexelsResults.length === 0 && (
              <p style={{ fontSize: '13px', color: '#718096', marginTop: '8px' }}>No photos found.</p>
            )}

            {pexelsResults.length > 0 && (
              <div className="brag-card-unsplash-grid">
                {pexelsResults.map((photo) => (
                  <button
                    key={photo.id}
                    type="button"
                    className={`brag-card-unsplash-thumb${backgroundPhotoPreview === photo.src.large ? ' selected' : ''}`}
                    onClick={() => onSetBackgroundPhotoFromUrl(photo.src.large)}
                    title={photo.photographer || ''}
                  >
                    <img src={photo.src.medium} alt={photo.alt || ''} crossOrigin="anonymous" />
                  </button>
                ))}
              </div>
            )}

            {pexelsResults.length > 0 && pexelsResults.length < pexelsTotalResults && (
              <button
                type="button"
                className="brag-card-search-btn brag-card-load-more-btn"
                onClick={handlePexelsLoadMore}
                disabled={pexelsLoadingMore}
                style={{ marginTop: '8px', width: '100%' }}
              >
                {pexelsLoadingMore ? 'Loading…' : 'Load more'}
              </button>
            )}

            <p style={{ fontSize: '11px', color: '#718096', marginTop: '8px' }}>
              Photos: <a href="https://www.pexels.com" target="_blank" rel="noreferrer">Pexels</a>
            </p>
          </div>
        )}

        {bgMode !== 'team-colors' && backgroundPhotoPreview && (
          <div style={{ marginTop: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#4a5568', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Image Position &amp; Scale
            </div>
            <div className="form-group">
              <label htmlFor="bgImageX">Left / Right</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Left</span>
                <input
                  id="bgImageX"
                  name="bgImageX"
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={formData.bgImageX ?? 50}
                  onChange={onChange}
                  style={{ flex: 1 }}
                />
                <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Right</span>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="bgImageY">Up / Down</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Up</span>
                <input
                  id="bgImageY"
                  name="bgImageY"
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={formData.bgImageY ?? 50}
                  onChange={onChange}
                  style={{ flex: 1 }}
                />
                <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Down</span>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="bgImageScale">
                Zoom
                <span style={{ fontSize: '12px', color: '#718096', marginLeft: '8px' }}>
                  ({formData.bgImageScale ?? 100}%)
                </span>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Out</span>
                <input
                  id="bgImageScale"
                  name="bgImageScale"
                  type="range"
                  min={10}
                  max={200}
                  step={5}
                  value={formData.bgImageScale ?? 100}
                  onChange={onChange}
                  style={{ flex: 1 }}
                />
                <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>In</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Dominant Image Upload — hidden for classic template ── */}
      {template !== 'classic' && template !== 'clean' && template !== 'finalScore' && (
        <div className="form-group">
          <label>
            Dominant Image (Optional)
            <span
              style={{
                fontSize: '12px',
                color: '#718096',
                fontWeight: 'normal',
                marginLeft: '8px',
              }}
            >
              (Player photo, team photo, or action shot)
            </span>
          </label>
          {dominantImageCropping ? (
            <InlineCropper
              imageSrc={dominantImageTempImage}
              onCropComplete={onDominantImageCropComplete}
              onCancel={onDominantImageCropCancel}
            />
          ) : dominantImagePreview ? (
            <>
              <style>{`@keyframes bg-removal-spin { to { transform: rotate(360deg); } }`}</style>
              <div className="background-photo-preview" style={{ position: 'relative' }}>
                <img src={dominantImagePreview} alt="Dominant" />
                {isRemoving && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'rgba(0,0,0,0.65)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 'inherit',
                      zIndex: 10,
                    }}
                  >
                    <div
                      style={{
                        width: '36px',
                        height: '36px',
                        border: '3px solid rgba(255,255,255,0.3)',
                        borderTopColor: '#fff',
                        borderRadius: '50%',
                        animation: 'bg-removal-spin 0.75s linear infinite',
                      }}
                    />
                    <p style={{ color: '#fff', fontSize: '13px', margin: '10px 0 0' }}>
                      Removing background…
                    </p>
                  </div>
                )}
                <button
                  type="button"
                  className="btn-edit-photo-overlay"
                  onClick={onEditDominantImageCrop}
                  disabled={saving || downloading || isRemoving}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="btn-remove-background-photo"
                  onClick={onRemoveDominantImage}
                  disabled={saving || downloading || isRemoving}
                >
                  ✕ Remove
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={canUseCutouts ? onRemoveDominantBg : undefined}
                  disabled={!canUseCutouts || isRemoving || saving || downloading}
                  style={
                    !canUseCutouts
                      ? { opacity: 0.5, cursor: 'not-allowed', marginTop: '8px', width: '100%' }
                      : { marginTop: '8px', width: '100%' }
                  }
                >
                  {isRemoving
                    ? 'Removing background…'
                    : canUseCutouts
                      ? '✂️ Remove Background'
                      : '🔒 Remove Background — Pro feature'}
                </button>
                {bgRemovalError && (
                  <p style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px' }}>
                    {bgRemovalError}
                  </p>
                )}
                {originalDominantImage && cutoutDominantImage && (
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <button
                      type="button"
                      className={activeDominantImageVersion === 'original' ? 'btn-primary' : 'btn-secondary'}
                      onClick={() => onToggleDominantImageVersion('original')}
                      style={{ flex: 1 }}
                      disabled={isRemoving}
                    >
                      Original
                    </button>
                    <button
                      type="button"
                      className={activeDominantImageVersion === 'cutout' ? 'btn-primary' : 'btn-secondary'}
                      onClick={() => onToggleDominantImageVersion('cutout')}
                      style={{ flex: 1 }}
                      disabled={isRemoving}
                    >
                      ✂️ Cutout
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <label htmlFor="recap-dominant-image" className="background-photo-upload-btn">
              <span>Upload Dominant Image</span>
              <input
                id="recap-dominant-image"
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={onDominantImageChange}
                style={{ display: 'none' }}
                disabled={saving || downloading}
              />
            </label>
          )}
        </div>
      )}

      {/* ── Image Controls — only visible when a dominant image is uploaded ── */}
      {dominantImagePreview && template !== 'clean' && template !== 'classic' && template !== 'finalScore' && (
        <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginTop: '8px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#4a5568', marginBottom: '12px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Image Controls
          </div>
          <div className="form-group">
            <label htmlFor="dominantImageX">Image Position — Left / Right</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Left</span>
              <input
                id="dominantImageX"
                name="dominantImageX"
                type="range"
                min={-100}
                max={200}
                step={5}
                value={formData.dominantImageX ?? 50}
                onChange={onChange}
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Right</span>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="dominantImageY">Image Position — Up / Down</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Up</span>
              <input
                id="dominantImageY"
                name="dominantImageY"
                type="range"
                min={-100}
                max={200}
                step={5}
                value={formData.dominantImageY ?? 50}
                onChange={onChange}
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Down</span>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="dominantImageScale">
              Image Size
              <span style={{ fontSize: '12px', color: '#718096', marginLeft: '8px' }}>
                ({formData.dominantImageScale ?? 100}%)
              </span>
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Smaller</span>
              <input
                id="dominantImageScale"
                name="dominantImageScale"
                type="range"
                min={10}
                max={300}
                step={5}
                value={formData.dominantImageScale ?? 100}
                onChange={onChange}
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Larger</span>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="dominantImageOpacity">
              Image Opacity
              <span style={{ fontSize: '12px', color: '#718096', marginLeft: '8px' }}>
                ({formData.dominantImageOpacity ?? 100}%)
              </span>
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Transparent</span>
              <input
                id="dominantImageOpacity"
                name="dominantImageOpacity"
                type="range"
                min={10}
                max={100}
                step={5}
                value={formData.dominantImageOpacity ?? 100}
                onChange={onChange}
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Solid</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default GameRecapFields;
